import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, Mail, Calendar, ArrowRight } from "lucide-react";
import { useMultiStepForm } from "../MultiStepFormContext";
import { SERVICE_PATHS } from "../questionConfig";

export function SuccessStep() {
  const { serviceType, contact, reset } = useMultiStepForm();
  const [showConfetti, setShowConfetti] = useState(true);

  const servicePath = SERVICE_PATHS.find((p) => p.id === serviceType);

  useEffect(() => {
    // Hide confetti after animation
    const timer = setTimeout(() => setShowConfetti(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  // Generate celebration emojis
  const renderConfetti = () => {
    if (!showConfetti) return null;

    const emojis = ["⚾", "🎉", "✨", "🙌", "👏", "🌟", "💫", "🔥", "✅", "⭐"];
    const elements = [];

    for (let i = 0; i < 20; i++) {
      const randomEmoji = emojis[Math.floor(Math.random() * emojis.length)];
      const randomLeft = Math.random() * 100;
      const randomDelay = Math.random() * 2;
      const randomSize = Math.random() * 1.5 + 1;

      elements.push(
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 100 }}
          animate={{
            opacity: [0, 1, 1, 0],
            y: [-20, -200],
          }}
          transition={{
            duration: 3,
            delay: randomDelay,
            ease: "easeOut",
          }}
          style={{
            position: "absolute",
            left: `${randomLeft}%`,
            bottom: 0,
            fontSize: `${randomSize}em`,
            pointerEvents: "none",
          }}
        >
          {randomEmoji}
        </motion.div>
      );
    }

    return elements;
  };

  return (
    <div className="relative text-center space-y-8 py-8">
      {/* Confetti */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {renderConfetti()}
      </div>

      {/* Success Icon */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", duration: 0.6 }}
        className="mx-auto w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center"
      >
        <CheckCircle className="w-12 h-12 text-green-500" />
      </motion.div>

      {/* Message */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-4"
      >
        <h2 className="text-[var(--dug-text-primary)] ds-type-control-heading">
          Thanks, {contact.name.split(" ")[0]}!
        </h2>
        <p className="text-[var(--dug-text-muted)] max-w-md mx-auto ds-type-body">
          We've received your {servicePath?.title.toLowerCase()} inquiry and will get back to you within 24 hours.
        </p>
      </motion.div>

      {/* What's Next */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-[var(--dug-bg-surface)] border border-[var(--dug-border)] rounded-2xl p-6 max-w-md mx-auto"
      >
        <h3 className="text-[var(--dug-text-primary)] mb-4 ds-type-card">What happens next?</h3>
        <div className="space-y-4 text-left">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-[var(--dug-coral)]/20 flex items-center justify-center flex-shrink-0">
              <Mail className="w-4 h-4 text-[var(--dug-coral)]" />
            </div>
            <div>
              <p className="text-[var(--dug-text-primary)] ds-type-body">Check your inbox</p>
              <p className="text-[var(--dug-text-muted)] ds-type-body">
                You'll receive a confirmation email at {contact.email}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-[var(--dug-coral)]/20 flex items-center justify-center flex-shrink-0">
              <Calendar className="w-4 h-4 text-[var(--dug-coral)]" />
            </div>
            <div>
              <p className="text-[var(--dug-text-primary)] ds-type-body">We'll be in touch</p>
              <p className="text-[var(--dug-text-muted)] ds-type-body">
                Our team will review your inquiry and reach out soon
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="flex flex-col sm:flex-row items-center justify-center gap-4"
      >
        <button
          onClick={reset}
          className="px-6 py-3 rounded-xl border-2 border-[var(--dug-border)] text-[var(--dug-text-primary)] font-medium hover:border-[var(--dug-coral)] transition-colors"
        >
          Submit Another Inquiry
        </button>
        <a
          href="https://godugout.com"
          className="px-6 py-3 rounded-xl bg-[var(--dug-coral)] text-[var(--dug-text-inverse)] font-medium flex items-center gap-2 hover:opacity-90 transition-opacity"
        >
          Back to Home
          <ArrowRight className="w-4 h-4" />
        </a>
      </motion.div>
    </div>
  );
}
